import type { ComputedRef } from 'vue';
import { FEATURE_MIN_TIER, TIER_LABEL, tierGateAllows, type Feature, type Tier } from '~/utils/billing/featureTiers';

export type TierGate = {
  /** Reactive: may the current user use this feature? True while gating is dormant. */
  allowed: ComputedRef<boolean>;
  /** The minimum tier this feature needs (Member / Pro). */
  requiredTier: Tier;
  requiredTierLabel: string;
  /** Open the subscription modal (the upgrade path). */
  open: () => void;
};

/**
 * Client-side tier gate. UX only — the authoritative lock is the server
 * `requireTier` helper. Use it to conditionally render a `<CuriosityLock>` or an
 * upgrade CTA around a gated surface:
 *
 *   const { gate } = useTierGate();
 *   const inventory = gate('inventory');
 *   // <div v-if="inventory.allowed">…real data…</div>
 *   // <CuriosityLock v-else :required-tier="inventory.requiredTier" @subscribe="inventory.open" />
 *
 * While `tierGatingEnabled` is false (dormant until B7), `allowed` is always
 * true, so nothing locks.
 */
export const useTierGate = () => {
  const { entitlements, tierGatingEnabled } = useEntitlements();
  const { openModal } = useBillingModal();
  const { previewPaywall } = usePaywallPreview();

  const gate = (feature: Feature): TierGate => {
    const requiredTier = FEATURE_MIN_TIER[feature];
    // `previewPaywall` (dev-only) force-locks every gated surface so the
    // free-user blur can be tested locally without flipping the prod flag.
    const allowed = computed(
      () => !previewPaywall.value && tierGateAllows(entitlements.value.can, feature, tierGatingEnabled.value),
    );
    // Open the subscription modal on the plans grid (same as the nav "Go Pro").
    // Picking a plan there fires `checkout_started`; we avoid deep-linking to a
    // plan's checkout step so the user sees the full comparison first.
    const open = () => openModal();
    return { allowed, requiredTier, requiredTierLabel: TIER_LABEL[requiredTier], open };
  };

  return { gate };
};
